import { faUserPlus } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { uniqBy } from "lodash";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button, Header, Message, Segment, Table } from "semantic-ui-react";
import { useNotifySettingsPublic } from "../../../../../../hooks/useNotifySettingsPublic";
import { useNotifySubscriberContactMethodsForLoggedInUser } from "../../../../../../hooks/useNotifySubscriberContactMethodsForLoggedInUser";
import { useNotifySubscriptionGroupsPublic } from "../../../../../../hooks/useNotifySubscriptionGroupsPublic";
import { SignUpModal } from "../modals/SignUpModal";
import { PreferenceCheckbox } from "./components/PreferenceCheckbox";

export function AnonymousSubscriptionGroupsTable() {
  const { site } = useParams<{ site: string }>();

  const [signUpModalData, setSignUpModalData] = useState<{ type: "Email" | "Phone" } | null>(null);

  const { subscriberContactMethods } = useNotifySubscriberContactMethodsForLoggedInUser();
  const subscriberContactMethod = subscriberContactMethods?.at(0);

  const { publicSubscriptionGroups } = useNotifySubscriptionGroupsPublic();
  const hasSubscriptionGroups = publicSubscriptionGroups && publicSubscriptionGroups?.length > 0;

  const subscriptionGroups = uniqBy(
    [...(publicSubscriptionGroups ?? []), ...(subscriberContactMethod?.subscriptionGroups ?? [])],
    "id",
  );

  const { isConfigured } = useNotifySettingsPublic();

  return (
    <>
      <SignUpModal data={signUpModalData} onClose={() => setSignUpModalData(null)} />
      <Message info className='tw-flex tw-items-center tw-gap-5'>
        <FontAwesomeIcon icon={faUserPlus} size='xl' />
        <div>
          <Message.Header>Sign Up to Better Manage Your Notifications</Message.Header>
          <Message.Content>
            <p>Add multiple contact methods, connect the mobile app, and more.</p>
            <Button
              primary
              as={Link}
              to={{
                pathname: `/${site}/public/register`,
                search: `?redirect=/${site}/public/notify/subscription-groups${
                  subscriberContactMethod?.type === "Email" ? `&email=${subscriberContactMethod?.identifier}` : ""
                }`,
              }}
            >
              Sign Up
            </Button>
          </Message.Content>
        </div>
      </Message>
      {!hasSubscriptionGroups && (
        <Message>
          <Message.Header>No Subscription Groups</Message.Header>
          <p>There are no subscription groups available for you to subscribe to.</p>
        </Message>
      )}
      {hasSubscriptionGroups && (
        <div>
          {/* Display table when screen is medium or larger */}
          <Table className='tw-hidden md:tw-table'>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Group</Table.HeaderCell>
                {isConfigured && <Table.HeaderCell className='tw-w-36'>SMS & Voice</Table.HeaderCell>}
                <Table.HeaderCell className='tw-w-36'>Email</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {subscriptionGroups?.map((group) => (
                <Table.Row key={group.id}>
                  <Table.Cell>
                    <Header as='h4' className='tw-m-0 tw-text-base'>
                      {group.name}
                    </Header>
                    <p className='tw-m-0 tw-text-black-light'>{group.description}</p>
                  </Table.Cell>
                  <Table.Cell>
                    {isConfigured && (
                      <PreferenceCheckbox
                        groupId={group.id}
                        type='Phone'
                        onOpenModal={() => setSignUpModalData({ type: "Phone" })}
                      />
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    <PreferenceCheckbox
                      groupId={group.id}
                      type='Email'
                      onOpenModal={() => setSignUpModalData({ type: "Email" })}
                    />
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          {/* Display cards when screen is smaller than medium */}
          <div className='tw-space-y-2 md:tw-hidden'>
            {subscriptionGroups?.map((group) => (
              <Segment key={group.id} className='tw-m-0 tw-flex tw-flex-col tw-gap-3'>
                <div className='tw-space-y-1'>
                  <Header as='h3' className='tw-m-0 tw-text-lg'>
                    {group.name}
                  </Header>
                  <p className='tw-m-0'>{group.description}</p>
                </div>
                <div className='tw-flex tw-flex-col tw-gap-2'>
                  {isConfigured && (
                    <PreferenceCheckbox
                      groupId={group.id}
                      label='SMS & Voice'
                      type='Phone'
                      onOpenModal={() => setSignUpModalData({ type: "Phone" })}
                    />
                  )}
                  <PreferenceCheckbox
                    groupId={group.id}
                    label='Email'
                    type='Email'
                    onOpenModal={() => setSignUpModalData({ type: "Email" })}
                  />
                </div>
              </Segment>
            ))}
          </div>
        </div>
      )}
    </>
  );
}
